import { ChangeDetectionStrategy, Component, computed, inject, viewChild } from "@angular/core";
import { EventFormStore } from "../../../standalone/event-form/event.form.store";
import {
    applyDateOccurrenceEdits,
    captureDateOccurrenceEdits,
    cloneDateOccurrence,
    type DateOccurrenceEdits,
} from "../../../standalone/event-form/date-occurrences/event.form.date.occurrences.types";
import type { EventForm } from "../../../standalone/event-form";
import { EventFormDateOccurrencesTableComponent } from "../../../standalone/event-form/date-occurrences/event.form.date.occurrences.table.component";
import { closeModalThenAnnounce } from "../modal.close.a11y.util";
import { ModalComponent } from "../modal.component";
import { ModalHeaderComponent } from "../modal.header.component";
import type { ModalData } from "../modal.service";

/** Open with `injector` from the form's scope so the modal and its table can inject {@link EventFormStore}. */
export interface EventFormDateOccurrencesModalData {
    date?: Date | null;
    dateFormat?: string;
    dateTimeFormat?: string;
    /** Invoked whenever the modal closes (footer or header) so AngJS can refresh */
    onClosed?: () => void | Promise<unknown>;
}

@Component({
    standalone: true,
    imports: [ModalHeaderComponent, EventFormDateOccurrencesTableComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <s25-modal-header [data]="headerParams()" />
        <div class="modal-body" (click)="onModalBodyClick($event)">
            <s25-ng-event-form-date-occurrences-table
                #occurrencesTable
                [date]="params()?.date ?? null"
                [dateFormat]="$safeNavigationMigration(params()?.dateFormat)"
                [dateTimeFormat]="$safeNavigationMigration(params()?.dateTimeFormat)"
                (closeRequested)="close()"
            />
        </div>
        <div class="modal-footer">
            <div class="aw-button-group">
                <button type="button" class="aw-button aw-button--outline" (click)="cancel()">Cancel</button>
                @if (params()?.date) {
                    <button type="button" class="aw-button aw-button--outline" (click)="addOccurrence()">
                        Add Occurrence
                    </button>
                }
                <button type="button" class="aw-button aw-button--primary" (click)="close()">Apply</button>
            </div>
        </div>
    `,
})
export class EventFormDateOccurrencesModal extends ModalComponent<EventFormDateOccurrencesModalData> {
    static readonly size = "lg" as const;
    static readonly title = "Date Occurrences";
    static readonly asDialog = true;
    static readonly windowClass = "event-form-date-occurrences";

    private readonly eventForm = inject(EventFormStore);
    private readonly occurrencesTable = viewChild<EventFormDateOccurrencesTableComponent>("occurrencesTable");
    private closeWrapped = false;
    private closingViaCancel = false;
    private occurrenceEditsByUuid = new Map<string, DateOccurrenceEdits>();
    private occurrenceClonesByUuid = new Map<string, EventForm.Occurrence>();
    private initialOccurrenceUuids: string[] = [];

    /** Header X discards edits (same as Cancel); footer Apply keeps edits. */
    readonly headerParams = computed(() => {
        const modalParams = this.params();
        if (!modalParams) return modalParams;
        return {
            ...modalParams,
            closeModal: () => this.cancel(),
        };
    });

    override set data(value: EventFormDateOccurrencesModalData & ModalData) {
        super.data = value;
        this.snapshotOccurrenceEdits(this.eventForm.model());
        this.wrapCloseModal(value);
    }

    override get data(): EventFormDateOccurrencesModalData & ModalData {
        return super.data;
    }

    close(): void {
        this.params()?.closeModal();
    }

    cancel(): void {
        this.closingViaCancel = true;
        this.restoreOccurrenceEdits();
        this.params()?.closeModal();
    }

    addOccurrence(): void {
        this.occurrencesTable()?.addOccurrence();
    }

    onModalBodyClick(event: MouseEvent): void {
        const removeButton = (event.target as HTMLElement).closest("[data-action='remove-occ']");
        if (!removeButton) {
            return;
        }
        const rowHost = removeButton.closest("[data-occ-uuid]");
        const occUuid = rowHost?.getAttribute("data-occ-uuid");
        if (occUuid) {
            this.occurrencesTable()?.removeOccurrenceByUuid(occUuid);
        }
    }

    private snapshotOccurrenceEdits(formModel: EventForm): void {
        const occurrences = formModel.occurrences ?? [];
        this.initialOccurrenceUuids = occurrences.map((occurrence) => String(occurrence.uuid));
        this.occurrenceEditsByUuid = new Map(
            occurrences.map((occurrence) => [
                String(occurrence.uuid),
                captureDateOccurrenceEdits(formModel, occurrence),
            ]),
        );
        this.occurrenceClonesByUuid = new Map(
            occurrences.map((occurrence) => [String(occurrence.uuid), cloneDateOccurrence(occurrence)]),
        );
    }

    private restoreOccurrenceEdits(): void {
        const formModel = this.eventForm.model();
        if (!formModel.occurrences) {
            return;
        }

        const currentOccurrences = [...formModel.occurrences];
        const liveByUuid = new Map(currentOccurrences.map((occurrence) => [String(occurrence.uuid), occurrence]));

        for (const occurrence of currentOccurrences) {
            if (!this.occurrenceClonesByUuid.has(String(occurrence.uuid))) {
                formModel.removeOccurrence?.(occurrence);
            }
        }

        const restoredOccurrences: EventForm.Occurrence[] = [];
        for (const uuid of this.initialOccurrenceUuids) {
            const initialEdits = this.occurrenceEditsByUuid.get(uuid);
            const initialClone = this.occurrenceClonesByUuid.get(uuid);
            if (!initialEdits || !initialClone) {
                continue;
            }

            const occurrence = liveByUuid.get(uuid) ?? cloneDateOccurrence(initialClone);
            applyDateOccurrenceEdits(formModel, occurrence, initialEdits);
            restoredOccurrences.push(occurrence);
        }

        formModel.occurrences.length = 0;
        formModel.occurrences.push(...restoredOccurrences);
        formModel.updateObjOcc?.();
        this.eventForm.bump();
    }

    private wrapCloseModal(modalParams: EventFormDateOccurrencesModalData & ModalData): void {
        if (this.closeWrapped || !modalParams.closeModal) {
            return;
        }
        this.closeWrapped = true;
        const originalCloseModal = modalParams.closeModal.bind(modalParams);
        const onClosed = modalParams.onClosed;
        modalParams.closeModal = (result?: unknown) => {
            const announcement = this.closeAnnouncementMessage();
            const finishClose = () => {
                try {
                    onClosed?.();
                } finally {
                    this.closingViaCancel = false;
                    originalCloseModal(result);
                }
            };
            closeModalThenAnnounce(announcement, finishClose);
        };
    }

    private closeAnnouncementMessage(): string {
        const formModel = this.eventForm.model();
        const occurrences = formModel.occurrences ?? [];
        const hasEdits =
            occurrences.some((occurrence) => {
                const initialEdits = this.occurrenceEditsByUuid.get(String(occurrence.uuid));
                if (!initialEdits) {
                    return true;
                }
                return (
                    JSON.stringify(captureDateOccurrenceEdits(formModel, occurrence)) !== JSON.stringify(initialEdits)
                );
            }) ||
            occurrences.length !== this.initialOccurrenceUuids.length ||
            this.initialOccurrenceUuids.some(
                (uuid) => !occurrences.some((occurrence) => String(occurrence.uuid) === uuid),
            );

        if (!this.closingViaCancel && hasEdits) {
            return "Changes applied Modal close";
        }
        return "Modal close";
    }
}
